/**
 * Le contrat de la double validation, côté navigateur (`EX-BO-003`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — les types suivent `BackOfficeMessages.PendingApprovalView`,
 * sans rien y ajouter. Un contrôle confronte champ par champ cette déclaration
 * au **fichier Java lui-même** : recopier un contrat, c'est en faire une
 * seconde source de vérité qui vieillira.
 *
 * ## Le défaut que ces deux appels referment
 *
 * `POST /api/backoffice/approvals/{id}/approval` était servi et éprouvé depuis
 * la story 2.3. **Rien, à aucune couche, ne listait les demandes en attente** —
 * ni dépôt, ni service, ni route, ni écran. On ne pouvait donc approuver qu'une
 * demande dont on connaissait déjà l'identifiant, alors que `EX-BO-003` exige
 * que le signataire soit une *autre* personne que le demandeur : précisément
 * celle qui ne le connaît pas. C'est la famille de défaut que `D-25`, `D-30` et
 * `D-35` décrivent, d'un degré plus grave — là-bas une route servie n'était
 * appelée par aucun écran ; ici la découverte elle-même manquait.
 *
 * ## Deux motifs, et il ne faut pas les confondre
 *
 * `reason` de [fetchPendingApprovals] est le motif d'**audit** de la
 * consultation : lire la file est un acte journalisé (`EX-BO-004`), et il est
 * constant — l'approbateur dépile toujours la même file. `reason` de
 * [approvePendingRequest] est le motif de la **décision** : il est écrit à la
 * main, demande par demande, et il finit dans l'entrée d'audit à côté des deux
 * identités. Les confondre inscrirait « file de double validation » comme motif
 * d'un déblocage de compte.
 */

import { ApiError } from './api-client.js';
import type { ApiClient } from './api-client.js';

/** Le chemin de la file, tel que `BackOfficeApi` le sert. */
export const pendingApprovalsPath = '/api/backoffice/approvals';

/** Le chemin de la décision d'une demande. */
export function approvalDecisionPath(requestId: string): string {
  return `${pendingApprovalsPath}/${requestId}/approval`;
}

/**
 * Le motif d'audit de la consultation de la file.
 *
 * Une **constante** et non une saisie, pour la raison exacte qui la rend
 * constante sur la revue KYC : l'approbateur ouvre toujours cette file pour la
 * même raison — voir ce qui attend sa signature. Une invite par ouverture
 * coûterait une frappe pour une phrase identique. Le motif de la *décision*,
 * lui, est écrit à chaque fois : voir l'en-tête.
 */
export const queueAuditReason = 'file-de-double-validation';

/**
 * Une demande en attente, exactement telle que le serveur la rend.
 *
 * `requestedBy` est un **identifiant**, pas un nom : le serveur rend l'`UUID` de
 * `admin.bo_user`. La maquette écrit « M. Ndiaye » ; aucune route ne sert ce
 * nom, et l'écrire ici l'inventerait. `actionType` et `status` sont de même des
 * **codes** — `ACCOUNT_UNBLOCK`, `EN_ATTENTE` — et non les phrases françaises du
 * dessin.
 *
 * **Aucune charge utile.** Le serveur ne la sert pas : le détail de l'action à
 * risque n'a pas à voyager vers chaque écran qui liste la file. Ce que cette
 * ligne porte est ce qui permet de *choisir* une demande.
 *
 * **Aucun compte de signatures.** La maquette dessine « 1 / 2 validations » ;
 * `admin.approval_request` n'a qu'un demandeur et qu'un approbateur, aucune
 * route ne sert de fraction, et l'écrire ici la fabriquerait. Voir
 * `DashboardScreen`, qui déclare l'omission.
 */
export interface PendingApproval {
  readonly id: string;
  readonly actionType: string;
  readonly entityRef: string;
  readonly requestedBy: string;
  readonly requestedAt: string;
  readonly requestedReason: string;
  readonly status: string;
}

/**
 * Lit les demandes en attente. Le motif est obligatoire : la lecture est
 * journalisée, et le serveur refuse un motif vide.
 *
 * Aucune capture d'erreur : l'`ApiError` du client porte déjà sa nature et sa
 * clé de message. L'avaler pour rendre une liste vide ferait confondre « rien
 * n'attend de signature » — un fait — avec « le serveur a refusé », qui n'en est
 * pas un. Sur cette file-ci, la confusion se lirait comme « il n'y a rien à
 * approuver », c'est-à-dire l'inverse de ce qui s'est passé.
 */
export async function fetchPendingApprovals(
  client: ApiClient,
  reason: string = queueAuditReason,
): Promise<readonly PendingApproval[]> {
  const recue = await client.request<unknown>(
    `${pendingApprovalsPath}?reason=${encodeURIComponent(reason)}`,
  );
  if (!estUneFile(recue)) {
    throw new ApiError({
      kind: 'server',
      status: null,
      correlationId: client.lastCorrelationId ?? '',
    });
  }
  return recue;
}

/**
 * La forme attendue, ligne par ligne. Un `unknown` non vérifié n'entre pas dans
 * l'écran.
 *
 * **Ce qui revient du réseau est vérifié avant d'être cru**, comme sur les
 * modules de l'opérateur. Une réponse à moitié formée — un objet là où un
 * tableau est attendu, une ligne sans identifiant — ferait lever l'écran au
 * rendu, c'est-à-dire une page blanche au lieu d'une file. Rendre une erreur
 * typée fait dire « le serveur a répondu autre chose qu'une file », ce qui est
 * exactement le cas, et n'est jamais confondu avec « rien n'attend de
 * signature ».
 */
function estUneFile(valeur: unknown): valeur is readonly PendingApproval[] {
  return (
    Array.isArray(valeur) &&
    valeur.every((ligne) => {
      if (typeof ligne !== 'object' || ligne === null) return false;
      const candidate = ligne as Record<string, unknown>;
      return (
        typeof candidate.id === 'string' &&
        typeof candidate.actionType === 'string' &&
        typeof candidate.entityRef === 'string' &&
        typeof candidate.requestedBy === 'string' &&
        typeof candidate.requestedAt === 'string' &&
        typeof candidate.requestedReason === 'string' &&
        typeof candidate.status === 'string'
      );
    })
  );
}

/**
 * Approuve une demande, avec le motif de la décision.
 *
 * **Le motif est obligatoire côté serveur**, et il l'est aussi côté écran : le
 * corps attendu est `ApprovalDecision(reason)`, et `requireReason` refuse une
 * chaîne vide. Ce n'est pas une validation de confort — c'est la moitié du
 * journal qui dit *pourquoi* une action à risque a été exécutée, et non
 * seulement qu'elle l'a été.
 *
 * **Cet appel n'obtient rien de plus que ce que la matrice accorde.** Le serveur
 * repose ses trois questions : l'habilitation sur l'action demandée, le refus de
 * l'auto-approbation, et la contrainte de base qui tient même quand
 * l'application est contournée. Un demandeur qui voit sa propre demande dans la
 * file ne peut pas davantage la signer.
 */
export async function approvePendingRequest(
  client: ApiClient,
  requestId: string,
  reason: string,
): Promise<void> {
  await client.request(approvalDecisionPath(requestId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}
